// Presets (SuiteCompile/Preset.swift): YAML with `inherits`, merged over `base`, decoded into `CompilePreset` and
// validated (`CompilePreset.validated`, the same messages). The built-ins are the Swift's YAML text, verbatim.
//
// Yams loads the merged mapping, dumps it and decodes it by type, so a scalar becomes whatever its field wants (an
// unquoted `5` is the string "5" for `sceneBreak`, the number 5 for `lineSpacing`). The `yaml` package reads every
// scalar here as text (the failsafe schema, remembering which were quoted), and `decode` converts each by its field
// as Yams would: YAML 1.1 booleans (`yes`, `on`…), `.inf`/`.nan`, `~`/`null` for nil.
import { parseDocument, isMap, isScalar, isSeq, type Node } from 'yaml';
import { BookMatter, isScriptKind, isStageFormat, scriptFamily, swiftCompare, type ProjectKind, type ScriptFormat } from '@writing-suite/core';
import { canonicalPageSize, formatted, formattedDouble, pageInches, resolvedMargins } from './manuscript.ts';
import {
  OUTPUT_FORMATS, SECTION_ROLES, type AudioSettings, type CompilePreset, type Condition, type MapRule, type OutputFormat,
  SCREENPLAY_PAGE_NUMBERS, LINK_HANDLINGS, type LinkHandling, type PrintSettings, type RenderSettings, type ScreenplaySettings,
  type SectionRole, type Select,
} from './types.ts';

// MARK: Errors (CompileError)

export type CompileErrorKind =
  | { type: 'invalidArchive' }
  | { type: 'unknownPreset'; id: string }
  | { type: 'presetCycle'; id: string }
  | { type: 'invalidPreset'; id: string; why: string }
  | { type: 'noArrangement'; name: string }
  | { type: 'engineUnavailable'; what: string }
  | { type: 'engineFailed'; why: string }
  | { type: 'noDocument'; title: string }
  | { type: 'emptyDocument'; title: string }
  | { type: 'noPost' }
  /** A checkpoint's fragments whose history hasn't reached this device: each one's title where this device has it. */
  | { type: 'missingFragments'; checkpoint: string; fragments: (string | null)[] };

/** `CompileError`, with the Swift's descriptions as messages. */
export class CompileError extends Error {
  constructor(readonly kind: CompileErrorKind) {
    super(CompileError.describe(kind));
    this.name = 'CompileError';
  }
  static describe(k: CompileErrorKind): string {
    switch (k.type) {
      case 'noDocument': return `This project has no “${k.title}” document in Publishing ▸ Submission`;
      case 'emptyDocument': return `“${k.title}” in Publishing ▸ Submission is empty. Write it, then compile`;
      case 'noPost': return 'Choose a post in the journal first';
      case 'missingFragments': {
        const n = k.fragments.length;
        const named = k.fragments.filter((t): t is string => t !== null && t.trim() !== '').map((t) => `“${t}”`);
        const shown = named.slice(0, 3);
        const rest = n - shown.length;
        const list = shown.length === 0 ? (n === 1 ? 'a document' : `${n} documents`)
          : rest > 0 ? `${shown.join(', ')} and ${rest} more` : shown.length > 1 ? `${shown.slice(0, -1).join(', ')} and ${shown.at(-1)}` : shown[0];
        return `The project as it was at “${k.checkpoint}” hasn’t all reached this device: ${list} ${n === 1 ? 'is' : 'are'} missing. Compile again once ${n === 1 ? 'it has' : 'they have'} synced`;
      }
      case 'invalidArchive': return 'The file is not a valid ZIP archive';
      case 'unknownPreset': return `No compile preset named “${k.id}”`;
      case 'presetCycle': return `Preset “${k.id}” inherits from itself`;
      case 'invalidPreset': return `Preset “${k.id}” is invalid: ${k.why}`;
      case 'noArrangement': return `No arrangement named “${k.name}”`;
      case 'engineUnavailable': return `${k.what} is not available in this build`;
      case 'engineFailed': return `Rendering failed: ${k.why}`;
    }
  }
}

const invalid = (id: string, why: string) => new CompileError({ type: 'invalidPreset', id, why });

// MARK: Built-ins (BuiltInPresets)

const base = `name: Base
description: Defaults every preset inherits.
format: docx
blind: false
select:
  arrangement: manuscript
  respectIncludeInCompile: true
  exclude: []
  frontMatter: false
  backMatter: false
map:
  rules:
    - when: { sectionType: Part }
      role: part
    - when: { sectionType: Chapter }
      role: chapter
    - when: { sectionType: Heading }
      role: chapter
    - when: { sectionType: Front Matter }
      role: front-matter
    - when: { sectionType: Back Matter }
      role: back-matter
    - when: { hasSubfolders: true }
      role: part
    - when: { hasChildren: true }
      role: chapter
    - when: {}
      role: scene
render:
  page: { size: letter, margins: 1 }
  font: { family: Times New Roman, size: 12 }
  lineSpacing: 2
  firstLineIndent: 0.5
  paragraphSpacing: 0
  justify: false
  sceneBreak: "#"
  header: ""
  titlePage: { include: true, contact: true, wordCount: true, wordCountRounding: 100, byline: true }
  partHeading: ["Part {numberWords}", "{title}"]
  chapterHeading: ["Chapter {numberWords}", "{title}"]
  chapterStartsNewPage: true
  chapterDrop: 2
  endMarker: END
  italicAsUnderline: false`;

const shunnDocx = `name: Standard manuscript (Shunn)
description: William Shunn's modern manuscript format as a Word document, for agents, editors and magazines.
format: docx
render:
  header: "{surname} / {shortTitle} / {page}"`;

const shunnPDF = `name: Standard manuscript (Shunn), PDF
description: The standard manuscript format as a PDF.
inherits: shunn-docx
format: pdf`;

const blindSubmission = `name: Blind submission
description: Standard manuscript with every trace of the author removed, for anonymous reading.
inherits: shunn-docx
blind: true
render:
  header: "{shortTitle} / {page}"
  titlePage: { contact: false, byline: false }`;

const readingCopy = `name: Reading copy
description: A typeset PDF for reading and review.
format: pdf
render:
  page: { size: a5, margins: 0.7 }
  font: { family: Palatino, size: 10.5 }
  lineSpacing: 1.3
  firstLineIndent: 0.25
  justify: true
  sceneBreak: "⁂"
  header: "{title}"
  titlePage: { contact: false, wordCount: false }
  chapterDrop: 1.2
  endMarker: ""`;

const printInterior = `name: Print interior (trade paperback)
description: A print-ready 5.5 × 8.5 in paperback interior with mirrored margins, running heads and chapters opening on the right, with the front and back matter.
format: pdf
select: { frontMatter: true, backMatter: true }
render:
  page: { size: 5.5x8.5, margins: 0.75, inside: 0.875, outside: 0.625, top: 0.7, bottom: 0.75 }
  font: { family: Libertinus Serif, size: 11 }
  lineSpacing: 1.25
  firstLineIndent: 0.25
  justify: true
  sceneBreak: ""
  header: ""
  titlePage: { contact: false, wordCount: false }
  partHeading: ["Part {numberWords}", "{title}"]
  chapterHeading: ["{number}", "{title}"]
  chapterDrop: 1.4
  endMarker: ""
  print:
    versoHead: "{author}"
    rectoHead: "{title}"
    chapterOpensRecto: true
    pageEdgeOrnament: "⁂"
    platform: kdp
    bleed: 0
    signature: 0
    preventWidows: true
    headingScale: 1.4
    balanceSpreads: true`;

/** `BuiltInPresets.poetryMap`: a poetry collection's map — a Poem is a chapter (its title its heading), a Section a part;
 *  the structure decides the rest as the base does. */
const poetryMap = `map:
  rules:
    - when: { sectionType: Poem }
      role: chapter
    - when: { sectionType: Section }
      role: part
    - when: { sectionType: Part }
      role: part
    - when: { sectionType: Chapter }
      role: chapter
    - when: { sectionType: Heading }
      role: chapter
    - when: { sectionType: Front Matter }
      role: front-matter
    - when: { sectionType: Back Matter }
      role: back-matter
    - when: { hasSubfolders: true }
      role: part
    - when: { hasChildren: true }
      role: chapter
    - when: {}
      role: scene`;

const poetryPrint = `name: Poetry collection (print)
description: A print-ready 5.5 × 8.5 in paperback of poems, each on its own page under its title, set ragged with no paragraph indents, with the front and back matter.
inherits: print-interior
${poetryMap}
render:
  firstLineIndent: 0
  justify: false
  sceneBreak: ""
  partHeading: ["{title}"]
  chapterHeading: ["{title}"]
  chapterDrop: 1
  chapterStartsNewPage: true
  keepChaptersWhole: true
  print:
    chapterOpensRecto: false`;

const poetryPrintRun = `name: Short poems, several a page
description: The paperback with the poems running on, a gap between them and no titles, each kept whole on a page when it fits. For haiku and other short forms.
inherits: poetry-print
render:
  chapterHeading: []
  chapterDrop: 0.35
  chapterStartsNewPage: false`;

const poetryPrintIngramSpark = `name: Poetry collection (IngramSpark)
description: The poetry paperback interior as PDF/X-1a:2001 for IngramSpark.
inherits: poetry-print
render:
  print:
    platform: ingramspark
    pdfx: x-1a`;

const poetryEbook = `name: Poetry collection (ebook)
description: EPUB 3 of the poems, each a chapter under its title so the reader's contents list them, set ragged with no paragraph indents, with the front and back matter.
inherits: ebook
${poetryMap}
render:
  firstLineIndent: 0
  justify: false
  sceneBreak: ""
  partHeading: ["{title}"]
  chapterHeading: ["{title}"]`;

const poetryManuscript = `name: Poetry manuscript (Word)
description: The poems as a Word document for journals and competitions, each starting a new page under its title, the verse single-spaced in named styles.
inherits: shunn-docx
${poetryMap}
render:
  firstLineIndent: 0
  sceneBreak: ""
  partHeading: ["{title}"]
  chapterHeading: ["{title}"]
  chapterStartsNewPage: true
  chapterDrop: 1
  endMarker: ""`;

const printInteriorIngramSpark = `name: Print interior (IngramSpark)
description: The trade paperback interior as PDF/X-1a:2001 for IngramSpark, with 0.5 in or wider margins all round.
inherits: print-interior
render:
  print:
    platform: ingramspark
    pdfx: x-1a`;

const synopsis = `name: Synopsis
description: "The Synopsis from Publishing ▸ Submission as a Word document for agents and publishers: title block, single-spaced, your name and the title in the header."
format: docx
select: { document: synopsis }
render:
  lineSpacing: 1
  firstLineIndent: 0
  paragraphSpacing: 12
  header: "{surname} / {shortTitle} / Synopsis / {page}"
  titlePage: { include: false }
  documentTitle: ["{title}", "Synopsis", "by {author}"]
  endMarker: ""`;

const synopsisPDF = `name: Synopsis, PDF
description: The Synopsis as a PDF.
inherits: synopsis
format: pdf`;

const pitch = `name: Pitch
description: "The Pitch from Publishing ▸ Submission as a Word document: title block, single-spaced, your name and the title in the header."
inherits: synopsis
select: { document: pitch }
render:
  header: "{surname} / {shortTitle} / Pitch / {page}"
  documentTitle: ["{title}", "Pitch", "by {author}"]`;

const logline = `name: Logline
description: The Logline from Publishing ▸ Submission as plain text, to paste into a form or an email.
format: text
select: { document: logline }
render:
  titlePage: { include: false }
  endMarker: ""`;

const queryLetter = `name: Query letter
description: "The Query Letter from Publishing ▸ Submission as a Word document: your contact details at the top, single-spaced block paragraphs."
format: docx
select: { document: query-letter }
render:
  lineSpacing: 1
  firstLineIndent: 0
  paragraphSpacing: 12
  header: ""
  titlePage: { include: false }
  letterhead: true
  endMarker: ""`;

const queryLetterText = `name: Query letter, plain text
description: The Query Letter as plain text, to paste into an email or a submission form.
inherits: query-letter
format: text
render:
  letterhead: false`;

const ebook = `name: Ebook
description: EPUB 3 for e-readers, with navigation and accessibility metadata, and the front and back matter.
format: epub
select: { frontMatter: true, backMatter: true }
render:
  sceneBreak: "⁂"
  firstLineIndent: 0.25
  lineSpacing: 1.3
  titlePage: { contact: false, wordCount: false }
  endMarker: ""`;

const audiobook = `name: Audiobook
description: "The book read aloud as one M4B file with a chapter for each part and chapter: opening credits, the dedication and epigraph, the story, and closing credits. Copyright, contents and other page furniture aren't read. Choose the voice below."
format: audiobook
select: { frontMatter: true, backMatter: true }
render:
  titlePage: { include: false }
  endMarker: ""
  audio:
    matter: [title-page, dedication, epigraph]
    closingCredits: true
    chapterHeading: ["Chapter {numberWords}", "{title}"]
    partHeading: ["Part {numberWords}", "{title}"]
    afterHeading: 1.5
    sceneBreak: 2
    paragraph: 0.6
    leadIn: 0.75
    tail: 2.5`;

const narrationScript = `name: Narration script
description: What the audiobook will say, as plain text with its tracks and pauses marked and your pronunciations applied, for checking or for a human narrator.
inherits: audiobook
format: text`;

const plainMarkdown = `name: Plain Markdown
description: The manuscript as one Markdown file.
format: markdown
render:
  sceneBreak: "***"
  endMarker: ""`;

const screenplay = `name: Screenplay
description: "The industry-standard screenplay PDF: Courier 12 on US Letter with a 1.5 in left margin, every element at its place, a title page, and (MORE) and (CONT'D) where a speech breaks across pages. A page runs about a minute."
format: pdf
render:
  page: { size: letter, margins: 1 }
  font: { family: Courier Prime, size: 12 }
  lineSpacing: 1
  firstLineIndent: 0
  titlePage: { include: true, contact: true, wordCount: false, byline: true }
  endMarker: ""
  screenplay: { sceneNumbers: false, continueds: true, boldSceneHeadings: false, sceneHeadingSpacing: 1 }
map:
  rules:
    - when: { hasChildren: true, depth: 0 }
      role: part
    - when: { hasChildren: true }
      role: chapter
    - when: {}
      role: scene`;

const screenplayA4 = `name: Screenplay (A4)
description: The screenplay on A4 paper, for the UK, Europe and Australia. Same margins and columns; a page holds a few more lines.
inherits: screenplay
render:
  page: { size: a4 }`;

const shootingScript = `name: Shooting script
description: The screenplay with scene numbers in both margins and bold scene headings, for production.
inherits: screenplay
render:
  screenplay: { sceneNumbers: true, boldSceneHeadings: true }`;

const screenplayFDX = `name: Final Draft
description: The screenplay as a Final Draft file (.fdx), with its title page, for producers and collaborators who use Final Draft.
inherits: screenplay
format: fdx`;

const screenplayFountain = `name: Fountain
description: The screenplay as one Fountain file (.fountain), plain text that Highland, Fade In, WriterDuet, Slugline and Beat all open.
inherits: screenplay
format: fountain`;

// Journals: each post a chapter headed by its title, its sections joined to it; folders (years, topics) are parts.
const journalMap = `map:
  rules:
    - when: { post: true }
      role: chapter
    - when: { inPost: true }
      role: scene
      join: true
    - when: { hasChildren: true }
      role: part
    - when: {}
      role: chapter`;

const postHTML = `name: Post, HTML
description: The chosen post with its opening and sign-off as one HTML page, to paste into Substack or another editor, or to send to whoever publishes it.
journal: true
format: html
select: { onePost: true, postMatter: true }
render:
  lineSpacing: 1.5
  firstLineIndent: 0
  paragraphSpacing: 12
  sceneBreak: "* * *"
  header: ""
  titlePage: { include: false }
  partHeading: ["{title}"]
  chapterHeading: ["{title}"]
  chapterStartsNewPage: false
  chapterDrop: 0
  endMarker: ""
${journalMap}`;

const postDocx = `name: Post, Word
description: The chosen post with its opening and sign-off as a Word document, single-spaced with space between paragraphs.
inherits: post-html
format: docx
render:
  lineSpacing: 1.15`;

const postMarkdown = `name: Post, Markdown
description: The chosen post with its opening and sign-off as Markdown. Merge tags such as [FIRSTNAME] are written as typed.
inherits: post-html
format: markdown`;

const postText = `name: Post, plain text
description: The chosen post with its opening and sign-off as plain text, to paste into an email or a form.
inherits: post-html
format: text`;

const journalEbook = `name: Journal as ebook
description: Every post, oldest first, as an EPUB with the journal's front and back matter; each post a chapter under its own title.
inherits: ebook
journal: true
select: { reversed: true }
render:
  partHeading: ["{title}"]
  chapterHeading: ["{title}"]
${journalMap}`;

const journalPrint = `name: Journal as paperback
description: Every post, oldest first, as a print-ready 5.5 × 8.5 in interior with the journal's front and back matter.
inherits: print-interior
journal: true
select: { reversed: true }
render:
  partHeading: ["{title}"]
  chapterHeading: ["{title}"]
  print: { chapterOpensRecto: false }
${journalMap}`;

const journalMarkdown = `name: All posts, Markdown
description: Every post, newest first as in the journal, each with its opening and sign-off, as one Markdown file.
inherits: post-markdown
select: { onePost: false }`;

const plainText = `name: Plain text
description: The manuscript as plain text.
format: text
render:
  sceneBreak: "* * *"
  endMarker: ""`;

// Stage plays (`design-docs/Stage plays — design.md`), as Preset.swift has them.
const stagePlayDocx = `name: Stage play (Word)
description: The play in US manuscript format as a Word document, each element a style of its own, for theatres and literary managers who ask for Word.
inherits: stage-play
format: docx`;

const stagePlayUKDocx = `name: Stage play (UK, Word)
description: The play in UK manuscript format as a Word document, each speech hanging after its name, for theatres that ask for Word.
inherits: stage-play-uk
format: docx`;

const stagePlay = `name: Stage play
description: "A play in US manuscript format: Courier 12 on US Letter, names above their speeches, dialogue the full width, directions indented in brackets, every act and scene on a new page, with a title page and a cast of characters. A page runs about a minute."
inherits: screenplay
select: { frontMatter: true }
render:
  screenplay: { format: stage-us, pageNumbers: act-scene-page, endMarkers: true }`;

const stagePlayA4 = `name: Stage play (A4)
description: The play on A4 paper. Same margins and columns; a page holds a few more lines.
inherits: stage-play
render:
  page: { size: a4 }`;

const stagePlayUK = `name: Stage play (UK)
description: "A play in UK manuscript format: Times New Roman 12 on A4, each name in bold at the margin with its speech beside it, directions in italics, every act and scene on a new page, with a title page and a cast of characters."
inherits: stage-play
render:
  page: { size: a4, margins: 1 }
  font: { family: Times New Roman, size: 12 }
  screenplay: { format: stage-uk, pageNumbers: plain, endMarkers: false }`;

const stagePlayFountain = `name: Fountain (play)
description: The play as one Fountain file (.fountain), with its acts as sections, for other scriptwriting apps.
inherits: stage-play
format: fountain`;

const stagePlaySample = `name: Ten-page sample
description: The first ten pages of the play in US manuscript format, with its title page, ending with the last speech or direction that ends on page ten. Theatres and competitions often ask for it.
inherits: stage-play
render:
  screenplay: { sample: { pages: 10 } }`;

const stagePlayUKSample = `name: Ten-page sample (UK)
description: The first ten pages of the play in UK manuscript format, with its title page, ending with the last speech or direction that ends on page ten.
inherits: stage-play-uk
render:
  screenplay: { sample: { pages: 10 } }`;

const characterBreakdown = `name: Character breakdown
description: "The play's Cast of Characters as a Word document, for theatres and casting: each character with a line about them. Made from who speaks when you haven't written it."
inherits: synopsis
select: { document: cast-list }
render:
  header: "{surname} / {shortTitle} / Characters / {page}"
  documentTitle: ["{title}", "Characters", "by {author}"]`;

const developmentHistory = `name: Development history
description: "The play's Development History from Publishing ▸ Submission as a Word document: its readings, workshops, productions and awards."
inherits: synopsis
select: { document: development-history }
render:
  header: "{surname} / {shortTitle} / Development History / {page}"
  documentTitle: ["{title}", "Development History", "by {author}"]`;

// Multi-camera sitcoms and radio plays (`design-docs/Musicals, radio and sitcoms — design.md`), as Preset.swift has them.
const sitcom = `name: Sitcom
description: "A multi-camera episode: Courier 12 on US Letter, each act and scene on a new page under its heading and letter, action in capitals with entrances and exits underlined, dialogue double-spaced. A page runs about thirty seconds."
inherits: screenplay
render:
  screenplay: { format: multicam, sceneLetterInHeader: false }`;

const sitcomDocx = `name: Sitcom (Word)
description: The episode as a Word document, each element a style of its own, for writers' rooms and producers who ask for Word.
inherits: sitcom
format: docx`;

const sitcomFountain = `name: Fountain (sitcom)
description: The episode as one Fountain file (.fountain), its acts and scenes as sections, for other scriptwriting apps.
inherits: sitcom
format: fountain`;

const radio = `name: Radio play
description: "A radio play in the BBC's scene style: Arial 12 on A4, scenes numbered, each name at the margin with its speech beside it, technical directions and sound cues in capitals and underlined, pages numbered at the foot, with a title page and a cast list. A page runs about a minute."
inherits: screenplay
select: { frontMatter: true }
render:
  page: { size: a4, margins: 1 }
  font: { family: Arial, size: 12 }
  screenplay: { format: radio, speechNumbers: none }`;

const radioStudio = `name: Radio play (studio)
description: The radio play for the studio, its speeches numbered from 1 on each page so the cast and the director can find their place.
inherits: radio
render:
  screenplay: { speechNumbers: page }`;

const radioDocx = `name: Radio play (Word)
description: The radio play in the BBC's scene style as a Word document, each element a style of its own, for producers who ask for Word.
inherits: radio
format: docx`;

const radioFountain = `name: Fountain (radio)
description: The radio play as one Fountain file (.fountain), its scenes as sections, for other scriptwriting apps.
inherits: radio
format: fountain`;

const musicalNumbers = `name: Musical numbers
description: "The musical's songs as a Word document, act by act, with who sings each: for theatres and licensing. Made from the songs when you haven't written the page."
inherits: synopsis
select: { document: musical-numbers }
render:
  header: "{surname} / {shortTitle} / Musical Numbers / {page}"
  documentTitle: ["{title}", "Musical Numbers", "by {author}"]`;

/** `BuiltInPresets.all`: raw YAML by id. */
export const BUILT_IN_PRESETS: Readonly<Record<string, string>> = {
  base, 'shunn-docx': shunnDocx, 'shunn-pdf': shunnPDF, 'blind-submission': blindSubmission, 'reading-copy': readingCopy,
  'print-interior': printInterior, 'print-interior-ingramspark': printInteriorIngramSpark, ebook,
  'poetry-print': poetryPrint, 'poetry-print-run': poetryPrintRun, 'poetry-print-ingramspark': poetryPrintIngramSpark,
  'poetry-ebook': poetryEbook, 'poetry-manuscript': poetryManuscript, synopsis,
  'synopsis-pdf': synopsisPDF, pitch, logline, 'query-letter': queryLetter, 'query-letter-text': queryLetterText,
  audiobook, 'narration-script': narrationScript, 'plain-markdown': plainMarkdown, 'plain-text': plainText,
  screenplay, 'screenplay-a4': screenplayA4, 'shooting-script': shootingScript, 'screenplay-fdx': screenplayFDX,
  'screenplay-fountain': screenplayFountain, 'post-html': postHTML, 'post-docx': postDocx, 'post-markdown': postMarkdown,
  'post-text': postText, 'journal-ebook': journalEbook, 'journal-print': journalPrint, 'journal-markdown': journalMarkdown,
  'stage-play': stagePlay,
  'stage-play-a4': stagePlayA4,
  'stage-play-uk': stagePlayUK,
  'stage-play-fountain': stagePlayFountain,
  'stage-play-docx': stagePlayDocx,
  'stage-play-uk-docx': stagePlayUKDocx,
  'stage-play-sample': stagePlaySample,
  'stage-play-uk-sample': stagePlayUKSample,
  sitcom,
  'sitcom-docx': sitcomDocx,
  'sitcom-fountain': sitcomFountain,
  'musical-numbers': musicalNumbers,
  radio,
  'radio-studio': radioStudio,
  'radio-docx': radioDocx,
  'radio-fountain': radioFountain,
  'character-breakdown': characterBreakdown,
  'development-history': developmentHistory,
};

// MARK: YAML as Yams sees it

/** A scalar as written: its text, and whether it was quoted (a quoted scalar is always a string). */
export class YScalar { constructor(readonly text: string, readonly quoted: boolean) {} }
export type YValue = YScalar | YValue[] | YMap;
export interface YMap { readonly [key: string]: YValue }
const scalar = (text: string, quoted = false) => new YScalar(text, quoted);

const isYScalar = (v: YValue | undefined): v is YScalar => v instanceof YScalar;
const isYMap = (v: YValue | undefined): v is YMap => !!v && !Array.isArray(v) && !isYScalar(v);

function fromNode(node: Node | null | undefined): YValue {
  if (node == null) return scalar('');
  if (isMap(node)) {
    const out: Record<string, YValue> = {};
    for (const pair of node.items) {
      const key = isScalar(pair.key) ? String(pair.key.value ?? '') : String(pair.key ?? '');
      out[key] = fromNode(pair.value as Node | null);
    }
    return out;
  }
  if (isSeq(node)) return node.items.map((item) => fromNode(item as Node | null));
  if (isScalar(node)) {
    const quoted = node.type === 'QUOTE_DOUBLE' || node.type === 'QUOTE_SINGLE' || node.type === 'BLOCK_LITERAL' || node.type === 'BLOCK_FOLDED';
    return scalar(String(node.value ?? ''), quoted);
  }
  return scalar('');
}

/** `Yams.load(yaml:)`: the document's value, or throws on a YAML error. */
export function loadYAML(source: string): YValue {
  const doc = parseDocument(source, { schema: 'failsafe', uniqueKeys: false, prettyErrors: false });
  if (doc.errors.length) throw new Error(doc.errors[0].message);
  return fromNode(doc.contents as Node | null);
}

const NULL = /^(?:~|null|Null|NULL|)$/;
const TRUE = /^(?:yes|Yes|YES|true|True|TRUE|on|On|ON)$/;
const FALSE = /^(?:no|No|NO|false|False|FALSE|off|Off|OFF)$/;
const isNull = (v: YValue | undefined) => v === undefined || (isYScalar(v) && !v.quoted && NULL.test(v.text));

/** A value as Yams resolves it: mappings, lists, strings, numbers and booleans (`PresetText.cleaned`'s tree). */
export type PValue = string | number | boolean | PValue[] | PMap;
export interface PMap { [key: string]: PValue }

/** A YAML value as Yams types it: a quoted scalar a string; a plain one null (dropped), a boolean, a number or a string. */
export function native(v: YValue): PValue | null {
  if (isYScalar(v)) {
    if (v.quoted) return v.text;
    if (NULL.test(v.text)) return null;
    if (TRUE.test(v.text)) return true;
    if (FALSE.test(v.text)) return false;
    const n = yamlNumber(v.text);
    return n !== null ? n : v.text;
  }
  if (Array.isArray(v)) return v.map(native).filter((x): x is PValue => x !== null);
  return nativeMap(v);
}

export function nativeMap(m: YMap): PMap {
  const out: PMap = {};
  for (const [k, v] of Object.entries(m)) {
    const x = native(v);
    if (x !== null) out[k] = x;
  }
  return out;
}

/** `deepMerge`: maps merge key by key, anything else replaces. */
export function deepMerge(base: YMap, override: YMap): YMap {
  const result: Record<string, YValue> = { ...base };
  for (const [key, value] of Object.entries(override)) {
    const existing = result[key];
    result[key] = isYMap(value) && isYMap(existing) ? deepMerge(existing, value) : value;
  }
  return result;
}

// MARK: Decoding by type

class Decode {
  constructor(readonly id: string) {}
  fail(path: string, why: string): never { throw invalid(this.id, `${path}: ${why}`); }

  map(v: YValue | undefined, path: string): YMap {
    if (v === undefined || isNull(v)) this.fail(path, 'is missing');
    if (!isYMap(v)) this.fail(path, 'expected a mapping');
    return v;
  }
  optionalMap(v: YValue | undefined, path: string): YMap | undefined { return isNull(v) ? undefined : this.map(v, path); }
  seq(v: YValue | undefined, path: string): YValue[] {
    if (v === undefined || isNull(v)) this.fail(path, 'is missing');
    if (!Array.isArray(v)) this.fail(path, 'expected a list');
    return v;
  }
  string(v: YValue | undefined, path: string): string {
    if (v === undefined || isNull(v)) this.fail(path, 'is missing');
    if (!isYScalar(v)) this.fail(path, 'expected a string');
    if (v.quoted) return v.text;
    // A plain scalar Yams resolved to another type is dumped in that type's form, then read back as text.
    if (TRUE.test(v.text)) return 'true';
    if (FALSE.test(v.text)) return 'false';
    if (/^[-+]?(0|[1-9][0-9_]*)$/.test(v.text)) return String(Number(v.text.replaceAll('_', '')));
    return v.text;
  }
  optionalString(v: YValue | undefined, path: string): string | undefined { return isNull(v) ? undefined : this.string(v, path); }
  bool(v: YValue | undefined, path: string): boolean {
    if (v === undefined || isNull(v)) this.fail(path, 'is missing');
    if (!isYScalar(v)) this.fail(path, 'expected true or false');
    if (TRUE.test(v.text)) return true;
    if (FALSE.test(v.text)) return false;
    return this.fail(path, `“${v.text}” isn't true or false`);
  }
  optionalBool(v: YValue | undefined, path: string): boolean | undefined { return isNull(v) ? undefined : this.bool(v, path); }
  double(v: YValue | undefined, path: string): number {
    if (v === undefined || isNull(v)) this.fail(path, 'is missing');
    if (!isYScalar(v)) this.fail(path, 'expected a number');
    const n = yamlNumber(v.text);
    if (n === null) this.fail(path, `“${v.text}” isn't a number`);
    return n;
  }
  optionalDouble(v: YValue | undefined, path: string): number | undefined { return isNull(v) ? undefined : this.double(v, path); }
  int(v: YValue | undefined, path: string): number {
    if (v === undefined || isNull(v)) this.fail(path, 'is missing');
    if (!isYScalar(v)) this.fail(path, 'expected a whole number');
    const t = v.text.replaceAll('_', '');
    if (!/^[-+]?\d+$/.test(t) && !/^[-+]?0x[0-9a-fA-F]+$/.test(t)) this.fail(path, `“${v.text}” isn't a whole number`);
    const n = Number(t);
    // Swift's Int is 64-bit: anything larger fails to decode.
    if (Math.abs(n) > 9223372036854775807) this.fail(path, `“${v.text}” is too large`);
    return n;
  }
  optionalInt(v: YValue | undefined, path: string): number | undefined { return isNull(v) ? undefined : this.int(v, path); }
  strings(v: YValue | undefined, path: string): string[] { return this.seq(v, path).map((s, i) => this.string(s, `${path}[${i}]`)); }
  optionalStrings(v: YValue | undefined, path: string): string[] | undefined { return isNull(v) ? undefined : this.strings(v, path); }
  oneOf<T extends string>(all: readonly T[], v: YValue | undefined, path: string): T {
    const s = this.string(v, path);
    if (!(all as readonly string[]).includes(s)) this.fail(path, `“${s}” isn't one of ${all.join(', ')}`);
    return s as T;
  }
}

/** A YAML 1.1 number as Yams resolves it: decimals and exponents (underscores allowed), hex, `.inf`, `.nan`. */
export function yamlNumber(text: string): number | null {
  const t = text.trim().replaceAll('_', '');
  if (/^[-+]?\.(inf|Inf|INF)$/.test(t)) return t.startsWith('-') ? -Infinity : Infinity;
  if (/^\.(nan|NaN|NAN)$/.test(t)) return NaN;
  if (/^[-+]?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?$/.test(t)) return Number(t);
  if (/^[-+]?0x[0-9a-fA-F]+$/.test(t)) return t.startsWith('-') ? -Number(t.slice(1)) : Number(t.replace('+', ''));
  return null;
}

function decodeCondition(d: Decode, v: YValue | undefined, path: string): Condition {
  const m = d.map(v, path);
  const c: Condition = {};
  const s = (k: keyof Condition) => d.optionalString(m[k], `${path}.${k}`);
  const collection = s('collection'), field = s('field'), equals = s('equals'), sectionType = s('sectionType'), kind = s('kind');
  const hasChildren = d.optionalBool(m.hasChildren, `${path}.hasChildren`);
  const hasSubfolders = d.optionalBool(m.hasSubfolders, `${path}.hasSubfolders`);
  const depth = d.optionalInt(m.depth, `${path}.depth`);
  const post = d.optionalBool(m.post, `${path}.post`), inPost = d.optionalBool(m.inPost, `${path}.inPost`);
  if (collection !== undefined) c.collection = collection;
  if (field !== undefined) c.field = field;
  if (equals !== undefined) c.equals = equals;
  if (sectionType !== undefined) c.sectionType = sectionType;
  if (hasChildren !== undefined) c.hasChildren = hasChildren;
  if (hasSubfolders !== undefined) c.hasSubfolders = hasSubfolders;
  if (depth !== undefined) c.depth = depth;
  if (kind !== undefined) c.kind = kind;
  if (post !== undefined) c.post = post;
  if (inPost !== undefined) c.inPost = inPost;
  return c;
}

const put = <T extends object, K extends keyof T>(o: T, k: K, v: T[K] | undefined) => { if (v !== undefined) o[k] = v; };

/** Decodes a merged mapping into `CompilePreset` (the synthesized `Decodable`: required keys must be there). */
export function decodePreset(id: string, merged: YMap): CompilePreset {
  const d = new Decode(id);
  const sel = d.map(merged.select, 'select');
  const select: Select = {
    arrangement: d.string(sel.arrangement, 'select.arrangement'),
    respectIncludeInCompile: d.bool(sel.respectIncludeInCompile, 'select.respectIncludeInCompile'),
    exclude: d.seq(sel.exclude, 'select.exclude').map((c, i) => decodeCondition(d, c, `select.exclude[${i}]`)),
    frontMatter: d.bool(sel.frontMatter, 'select.frontMatter'),
    backMatter: d.bool(sel.backMatter, 'select.backMatter'),
  };
  if (!isNull(sel.include)) select.include = d.seq(sel.include, 'select.include').map((c, i) => decodeCondition(d, c, `select.include[${i}]`));
  put(select, 'document', d.optionalString(sel.document, 'select.document'));
  put(select, 'postMatter', d.optionalBool(sel.postMatter, 'select.postMatter'));
  put(select, 'reversed', d.optionalBool(sel.reversed, 'select.reversed'));
  put(select, 'onePost', d.optionalBool(sel.onePost, 'select.onePost'));

  const mp = d.map(merged.map, 'map');
  const rules: MapRule[] = d.seq(mp.rules, 'map.rules').map((r, i) => {
    const rule = d.map(r, `map.rules[${i}]`);
    const out: MapRule = { when: decodeCondition(d, rule.when, `map.rules[${i}].when`), role: d.oneOf<SectionRole>(SECTION_ROLES, rule.role, `map.rules[${i}].role`) };
    put(out, 'join', d.optionalBool(rule.join, `map.rules[${i}].join`));
    return out;
  });

  const r = d.map(merged.render, 'render');
  const page = d.map(r.page, 'render.page');
  const font = d.map(r.font, 'render.font');
  const tp = d.map(r.titlePage, 'render.titlePage');
  const render: RenderSettings = {
    page: { size: d.string(page.size, 'render.page.size'), margins: d.double(page.margins, 'render.page.margins') },
    font: { family: d.string(font.family, 'render.font.family'), size: d.double(font.size, 'render.font.size') },
    lineSpacing: d.double(r.lineSpacing, 'render.lineSpacing'),
    firstLineIndent: d.double(r.firstLineIndent, 'render.firstLineIndent'),
    paragraphSpacing: d.double(r.paragraphSpacing, 'render.paragraphSpacing'),
    justify: d.bool(r.justify, 'render.justify'),
    sceneBreak: d.string(r.sceneBreak, 'render.sceneBreak'),
    header: d.string(r.header, 'render.header'),
    titlePage: {
      include: d.bool(tp.include, 'render.titlePage.include'),
      contact: d.bool(tp.contact, 'render.titlePage.contact'),
      wordCountRounding: d.int(tp.wordCountRounding, 'render.titlePage.wordCountRounding'),
      wordCount: d.bool(tp.wordCount, 'render.titlePage.wordCount'),
      byline: d.bool(tp.byline, 'render.titlePage.byline'),
    },
    partHeading: d.strings(r.partHeading, 'render.partHeading'),
    chapterHeading: d.strings(r.chapterHeading, 'render.chapterHeading'),
    chapterStartsNewPage: d.bool(r.chapterStartsNewPage, 'render.chapterStartsNewPage'),
    chapterDrop: d.double(r.chapterDrop, 'render.chapterDrop'),
    endMarker: d.string(r.endMarker, 'render.endMarker'),
    italicAsUnderline: d.bool(r.italicAsUnderline, 'render.italicAsUnderline'),
  };
  for (const k of ['inside', 'outside', 'top', 'bottom'] as const) put(render.page, k, d.optionalDouble(page[k], `render.page.${k}`));
  put(render, 'keepChaptersWhole', d.optionalBool(r.keepChaptersWhole, 'render.keepChaptersWhole'));
  const pr = d.optionalMap(r.print, 'render.print');
  if (pr) {
    const print: PrintSettings = {
      versoHead: d.string(pr.versoHead, 'render.print.versoHead'),
      rectoHead: d.string(pr.rectoHead, 'render.print.rectoHead'),
      chapterOpensRecto: d.bool(pr.chapterOpensRecto, 'render.print.chapterOpensRecto'),
      pageEdgeOrnament: d.string(pr.pageEdgeOrnament, 'render.print.pageEdgeOrnament'),
      platform: d.string(pr.platform, 'render.print.platform'),
      bleed: d.double(pr.bleed, 'render.print.bleed'),
      signature: d.int(pr.signature, 'render.print.signature'),
      preventWidows: d.bool(pr.preventWidows, 'render.print.preventWidows'),
      headingScale: d.double(pr.headingScale, 'render.print.headingScale'),
      balanceSpreads: d.bool(pr.balanceSpreads, 'render.print.balanceSpreads'),
    };
    put(print, 'pdfx', d.optionalString(pr.pdfx, 'render.print.pdfx'));
    render.print = print;
  }
  put(render, 'documentTitle', d.optionalStrings(r.documentTitle, 'render.documentTitle'));
  put(render, 'letterhead', d.optionalBool(r.letterhead, 'render.letterhead'));
  const au = d.optionalMap(r.audio, 'render.audio');
  if (au) {
    const audio: AudioSettings = {
      matter: d.strings(au.matter, 'render.audio.matter'),
      closingCredits: d.bool(au.closingCredits, 'render.audio.closingCredits'),
      chapterHeading: d.strings(au.chapterHeading, 'render.audio.chapterHeading'),
      partHeading: d.strings(au.partHeading, 'render.audio.partHeading'),
      afterHeading: d.double(au.afterHeading, 'render.audio.afterHeading'),
      sceneBreak: d.double(au.sceneBreak, 'render.audio.sceneBreak'),
      paragraph: d.double(au.paragraph, 'render.audio.paragraph'),
      leadIn: d.double(au.leadIn, 'render.audio.leadIn'),
      tail: d.double(au.tail, 'render.audio.tail'),
    };
    render.audio = audio;
  }
  const sp = d.optionalMap(r.screenplay, 'render.screenplay');
  if (sp) {
    const screenplay: ScreenplaySettings = {
      sceneNumbers: d.bool(sp.sceneNumbers, 'render.screenplay.sceneNumbers'),
      continueds: d.bool(sp.continueds, 'render.screenplay.continueds'),
      boldSceneHeadings: d.bool(sp.boldSceneHeadings, 'render.screenplay.boldSceneHeadings'),
      sceneHeadingSpacing: d.int(sp.sceneHeadingSpacing, 'render.screenplay.sceneHeadingSpacing'),
    };
    const format = d.optionalString(sp.format, 'render.screenplay.format');
    if (format !== undefined) screenplay.format = format;
    const pageNumbers = d.optionalString(sp.pageNumbers, 'render.screenplay.pageNumbers');
    if (pageNumbers !== undefined) screenplay.pageNumbers = pageNumbers;
    const endMarkers = d.optionalBool(sp.endMarkers, 'render.screenplay.endMarkers');
    if (endMarkers !== undefined) screenplay.endMarkers = endMarkers;
    const continuousScenes = d.optionalBool(sp.continuousScenes, 'render.screenplay.continuousScenes');
    if (continuousScenes !== undefined) screenplay.continuousScenes = continuousScenes;
    const musical = d.optionalBool(sp.musical, 'render.screenplay.musical');
    if (musical !== undefined) screenplay.musical = musical;
    const letters = d.optionalBool(sp.sceneLetterInHeader, 'render.screenplay.sceneLetterInHeader');
    if (letters !== undefined) screenplay.sceneLetterInHeader = letters;
    const speechNumbers = d.optionalString(sp.speechNumbers, 'render.screenplay.speechNumbers');
    if (speechNumbers !== undefined) screenplay.speechNumbers = speechNumbers;
    const sample = d.optionalMap(sp.sample, 'render.screenplay.sample');
    if (sample) {
      screenplay.sample = { pages: d.int(sample.pages, 'render.screenplay.sample.pages') };
      const from = d.optionalString(sample.from, 'render.screenplay.sample.from');
      if (from !== undefined) screenplay.sample.from = from;
    }
    render.screenplay = screenplay;
  }
  // A value the dialect doesn't know is refused, never quietly ignored.
  if (!isNull(r.links)) render.links = d.oneOf<LinkHandling>(LINK_HANDLINGS, r.links, 'render.links');
  put(render, 'keepHighlights', d.optionalBool(r.keepHighlights, 'render.keepHighlights'));

  const preset: CompilePreset = {
    id: d.string(merged.id, 'id'),
    name: d.string(merged.name, 'name'),
    description: d.string(merged.description, 'description'),
    format: d.oneOf<OutputFormat>(OUTPUT_FORMATS, merged.format, 'format'),
    blind: d.bool(merged.blind, 'blind'),
    select,
    map: { rules },
    render,
  };
  put(preset, 'journal', d.optionalBool(merged.journal, 'journal'));
  put(preset, 'edition', d.optionalString(merged.edition, 'edition'));
  // Declaration order, as the Swift encodes it.
  const { id: pid, name, description, format, blind, journal, edition } = preset;
  return { id: pid, name, description, format, blind, ...(journal !== undefined ? { journal } : {}),
    ...(edition !== undefined ? { edition } : {}), select, map: preset.map, render };
}

// MARK: Validation (CompilePreset.validated)

/** Swift's `Double.description`: "3.0", "1e+16", "inf", "nan". */
export function swiftDoubleDescription(x: number): string {
  if (Number.isNaN(x)) return 'nan';
  if (x === Infinity) return 'inf';
  if (x === -Infinity) return '-inf';
  const a = Math.abs(x);
  if (a !== 0 && (a >= 1e16 || a < 1e-4)) {
    const [m, e] = x.toExponential().split('e');
    const exp = Number(e);
    return `${m}e${exp < 0 ? '-' : '+'}${String(Math.abs(exp)).padStart(2, '0')}`;
  }
  return Number.isInteger(x) ? `${x}.0` : String(x);
}

/** The preset with its page size canonical, or `invalidPreset` naming the first number out of range. */
export function validated(p: CompilePreset): CompilePreset {
  const id = p.id;
  const check = (value: number, lo: number, hi: number, key: string, unit: string) => {
    if (!(Number.isFinite(value) && value >= lo && value <= hi)) {
      throw invalid(id, `${key} must be between ${formattedDouble(lo)} and ${formattedDouble(hi)}${unit} (it is ${swiftDoubleDescription(value)})`);
    }
  };
  const checkInt = (value: number, lo: number, hi: number, key: string) => {
    if (!(value >= lo && value <= hi)) throw invalid(id, `${key} must be between ${lo} and ${formatted(hi)} (it is ${value})`);
  };
  const r = p.render;
  const size = canonicalPageSize(r.page.size);
  if (size === null) {
    throw invalid(id, `page size “${r.page.size}” isn't letter, a4, a5 or a width and height in inches such as 6x9`);
  }
  const preset: CompilePreset = structuredClone(p);
  preset.render.page.size = size;
  const { width, height } = pageInches(size);
  check(r.page.margins, 0, 4, 'page margins', ' in');
  for (const key of ['inside', 'outside', 'top', 'bottom'] as const) {
    const value = r.page[key];
    if (value !== undefined) check(value, 0, 4, `page ${key} margin`, ' in');
  }
  const m = resolvedMargins(r.page);
  if (!(width - Math.max(m.inside + m.outside, 2 * r.page.margins) >= 1 && height - Math.max(m.top + m.bottom, 2 * r.page.margins) >= 1)) {
    throw invalid(id, `the margins leave less than an inch of the ${size} page for text`);
  }
  check(r.font.size, 4, 96, 'font size', ' pt');
  check(r.lineSpacing, 0.5, 4, 'lineSpacing', '');
  check(r.firstLineIndent, 0, 4, 'firstLineIndent', ' in');
  check(r.paragraphSpacing, 0, 144, 'paragraphSpacing', ' pt');
  check(r.chapterDrop, 0, height, 'chapterDrop', ' in');
  checkInt(r.titlePage.wordCountRounding, 0, 1_000_000, 'titlePage wordCountRounding');
  if (r.print) {
    check(r.print.bleed, 0, 1, 'print bleed', ' in');
    checkInt(r.print.signature, 0, 256, 'print signature');
    check(r.print.headingScale, 0.5, 5, 'print headingScale', '');
  }
  if (r.screenplay) {
    checkInt(r.screenplay.sceneHeadingSpacing, 0, 3, 'screenplay sceneHeadingSpacing');
    const style = r.screenplay.pageNumbers;
    if (style !== undefined && !SCREENPLAY_PAGE_NUMBERS.includes(style)) {
      throw invalid(id, `screenplay pageNumbers is “${style}”: use act-scene-page or plain`);
    }
    if (r.screenplay.sample) checkInt(r.screenplay.sample.pages, 1, 500, 'screenplay sample pages');
  }
  if (r.audio) {
    const a = r.audio;
    for (const [value, key] of [[a.afterHeading, 'afterHeading'], [a.sceneBreak, 'sceneBreak'], [a.paragraph, 'paragraph'],
      [a.leadIn, 'leadIn'], [a.tail, 'tail']] as const) check(value, 0, 30, `audio ${key}`, ' s');
  }
  return preset;
}

// MARK: The library (PresetLibrary)

export class PresetLibrary {
  /** Raw YAML by id: the built-ins, with the project's own over them. */
  readonly sources: Readonly<Record<string, string>>;

  constructor(additional: Record<string, string> = {}) {
    this.sources = { ...BUILT_IN_PRESETS, ...additional };
  }

  get ids(): string[] { return Object.keys(this.sources).filter((id) => id !== 'base').sort(swiftCompare); }

  presets(): CompilePreset[] {
    return this.ids.flatMap((id) => { try { return [this.preset(id)]; } catch { return []; } });
  }

  /** Presets that can't be read, and why. */
  problems(): { id: string; message: string }[] {
    return this.ids.flatMap((id) => {
      try { this.preset(id); return []; } catch (e) { return [{ id, message: (e as Error).message }]; }
    });
  }

  preset(id: string): CompilePreset {
    const merged: Record<string, YValue> = { ...this.resolved(id, new Set()) };
    merged.id = scalar(id, true);
    return validated(decodePreset(id, merged));
  }

  /** The preset's settings with everything it inherits, as Yams reads them (no `inherits`, no `id`): `PresetLibrary.values`. */
  values(id: string): PMap {
    return nativeMap(this.resolved(id, new Set()));
  }

  /** The preset this one starts from: its `inherits`, else base (null for base itself, or text that can't be read). */
  parent(id: string): string | null {
    const source = this.sources[id];
    if (source === undefined) return null;
    try {
      const dict = loadYAML(source);
      if (!isYMap(dict)) return null;
      const inherits = dict.inherits;
      return isYScalar(inherits) && !isNull(inherits) ? inherits.text : id === 'base' ? null : 'base';
    } catch {
      return null;
    }
  }

  private resolved(id: string, seen: Set<string>): YMap {
    if (seen.has(id)) throw new CompileError({ type: 'presetCycle', id });
    const source = this.sources[id];
    if (source === undefined) throw new CompileError({ type: 'unknownPreset', id });
    let dict: YValue;
    try {
      dict = loadYAML(source);
    } catch (e) {
      throw invalid(id, (e as Error).message);
    }
    if (!isYMap(dict)) throw invalid(id, 'not a YAML mapping');
    const inherits = dict.inherits;
    const parent = isYScalar(inherits) && !isNull(inherits) ? inherits.text : id === 'base' ? null : 'base';
    const base: Record<string, YValue> = parent !== null ? { ...this.resolved(parent, new Set([...seen, id])) } : {};
    delete base.inherits;
    const own: Record<string, YValue> = { ...dict };
    delete own.inherits;
    return deepMerge(base, own);
  }
}

/** The presets a project of this kind sees (`CompilePreset.isOffered(to:format:musical:projectPresetIDs:)`): journals
 *  only theirs and their own, scripts scripts in their own family of formats (a film script never a sitcom's or a play's,
 *  a play never a radio play's; a musical's own presets a musical's only) and the submission documents, books everything
 *  else. `own`: the project's own preset ids; `format`: the project's script format (absent: the kind's usual one). */
export function presetsFor(presets: CompilePreset[], kind: ProjectKind, own: Iterable<string> = [],
  o: { format?: ScriptFormat; musical?: boolean } = {}): CompilePreset[] {
  const mine = new Set(own);
  const musical = o.musical ?? false;
  const projectFormat = o.format ?? (kind === 'stage-play' ? 'stage-us' : kind === 'radio-play' ? 'radio' : 'screen');
  return presets.filter((p) => {
    if (p.journal === true) return kind === 'journal';
    // A play's own pages (the character breakdown, its development history) are its kind's only.
    const document = p.select.document !== undefined ? BookMatter.from(p.select.document) : null;
    if (document && BookMatter.stageOnly.has(document)) return BookMatter.belongs(document, kind, musical);
    if (p.render.screenplay) {
      if (!isScriptKind(kind)) return false;
      if (p.render.screenplay.musical === true && !musical) return false;
      return scriptFamily(p.render.screenplay.format ?? 'screen') === scriptFamily(projectFormat);
    }
    switch (kind) {
      case 'manuscript': return true;
      case 'screenplay': case 'stage-play': case 'radio-play': return p.select.document !== undefined;
      case 'journal': return mine.has(p.id);
    }
  });
}

/** `ProjectDocument.compilePresets()`: the project's own preset YAML by id, from `settings.compilePresets`. */
export function projectPresetSources(settings: Record<string, unknown>): Record<string, string> {
  const raw = settings.compilePresets;
  const out: Record<string, string> = {};
  if (raw && typeof raw === 'object') for (const [id, v] of Object.entries(raw as Record<string, unknown>)) if (typeof v === 'string') out[id] = v;
  return out;
}
